import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { goToLogin, onSessionExpired } from '../../utils/sessionExpiry';
import './EditorConnectionBanner.css';

/**
 * When the editor cannot reach Flow, what it shows may be an older copy of the file (the browser's own), and what is
 * typed would not be saved. So the editor says so and reads only until it is connected again (MonacoFileEditor):
 *
 * - 'expired': the Flow session expired (utils/sessionExpiry.js) — sign in again;
 * - 'offline': the browser is offline, or the file's live connection was lost or never made (useCollaboration's
 *   'disconnected' / 'unavailable'; it reconnects by itself and then loads the file as it is on the server).
 *
 * `connectionState(status, { online, expired })` is the rule; `useEditorConnection(status)` follows the browser and the
 * session. `status` is useCollaboration's ('off' when live editing is not used for this file: nothing to say then).
 */
export function connectionState(status, { online = true, expired = false } = {}) {
  if (!status || status === 'off') { return 'ok'; }
  if (expired) { return 'expired'; }
  if (!online || status === 'unavailable' || status === 'disconnected') { return 'offline'; }
  return 'ok';
}

const browserOnline = () => (typeof navigator === 'undefined' || navigator.onLine !== false);

/** `{ state, blocked }`: blocked (the editor reads only) whenever the state is not 'ok'. */
export function useEditorConnection(status) {
  const [online, setOnline] = useState(browserOnline);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const update = () => setOnline(browserOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => onSessionExpired(() => setExpired(true)), []);

  const state = connectionState(status, { online, expired });
  return { state, blocked: state !== 'ok' };
}

export default function EditorConnectionBanner({ connection }) {
  if (!connection || connection.state === 'ok') { return null; }
  if (connection.state === 'expired') {
    return (
      <div className="editor-connection-banner is-expired" data-testid="editor-connection-banner" role="alert">
        <span>Your session expired — sign in again to see the latest version. Editing is paused until then.</span>
        <Button onClick={goToLogin} size="small" type="primary">Sign in again</Button>
      </div>
    );
  }
  return (
    <div className="editor-connection-banner is-offline" data-testid="editor-connection-banner" role="alert">
      <span>{"You're offline — reconnect to see the latest version. Editing is paused until then."}</span>
    </div>
  );
}

EditorConnectionBanner.propTypes = {
  connection: PropTypes.shape({ state: PropTypes.string, blocked: PropTypes.bool }),
};
